import * as React from 'react';
import SegmentTabs from '../../../../SegmentTabs/SegmentTabs';
import { useTranslation } from 'react-i18next';
import { NASStatus, OrderStatus } from '@websky/graphql';
import EveryPortCard from '../../../../EveryPortCard/EveryPortCard/EveryPortCard';
import { useTheme } from '../../../../theme';
import { format, MOBILE_MIN_WIDTH } from '../../../../utils';
import MediaQuery from 'react-responsive';
import Toolbar from '../../../../Toolbar';
import SimpleLoader from '../../../../SimpleLoader';
import Options from '../Options/Options';
import SelectedService from './SelectedService/SelectedService';
import { filterServicesForAdultTravellerExists, findMinPrice, getEveryPortSelectedServices } from '../utils';
import { PassengerType } from '../../../../enums';
import Rules from '../Rules/Rules';
import { useEveryPortState } from './useEveryPortState.hook';
import { useConfig } from '../../../../context';
import { useSelector } from 'react-redux';
import { getOrderStatus } from '../../../../Checkout/store/order/selectors';
import WebskyAnalyticsApollo from '../../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { WxEvents } from '../../../../DataLayerAnalytics/types';
const Inner = ({ everyPortServices, segments, selectedServices, onServiceSave, onClose, segmentId: defaultSegmentId, loading, travellers, type, header, readonly }) => {
    const { t } = useTranslation('ServiceAggregation'), { nasServiceUpsaleForConfirmedOrder } = useConfig().Engine, orderStatus = useSelector(getOrderStatus);
    const { state, setTraveller, removeTraveller, clearSegment } = useEveryPortState(selectedServices, everyPortServices);
    const [segmentId, setSegmentId] = React.useState(defaultSegmentId !== null && defaultSegmentId !== void 0 ? defaultSegmentId : segments[0].id);
    const [selectedServiceId, setSelectedServiceId] = React.useState(null);
    const modifyAvailable = orderStatus === OrderStatus.New ||
        orderStatus === OrderStatus.Booked ||
        (nasServiceUpsaleForConfirmedOrder && orderStatus !== OrderStatus.Cancelled);
    const selectedService = React.useMemo(() => {
        return everyPortServices.find(service => service.id === selectedServiceId);
    }, [selectedServiceId]);
    const theme = useTheme('CheckoutAdditionalService').EveryPortLounge;
    const onServiceSelect = (service) => {
        if (service.id === selectedServiceId) {
            setSelectedServiceId(null);
            clearSegment(service.segmentId.toString());
        }
        else {
            setSelectedServiceId(service.id);
            WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.NASVipItem, {
                segmentId: service.segmentId.toString(),
                servicesIds: [service.id]
            });
        }
    };
    const tabs = React.useMemo(() => {
        const segmentTabs = [];
        everyPortServices.forEach(service => {
            if (!segmentTabs.some(segment => segment.id === service.segmentId.toString())) {
                const segment = segments.find(segment => segment.id === service.segmentId.toString());
                segmentTabs.push({
                    id: segment.id,
                    title: segment.departure.airport.city.name,
                    isDisabled: false,
                    countSeatsSelected: state.reduce((count, item) => {
                        if (item.product.segmentId.toString() === segment.id) {
                            return count + 1;
                        }
                        return count;
                    }, 0)
                });
            }
        });
        return segmentTabs;
    }, [everyPortServices, segments, state]);
    const segment = segments.find(segment => segment.id === segmentId);
    const onNextClick = () => {
        onServiceSave(getEveryPortSelectedServices(filterServicesForAdultTravellerExists(state, travellers)));
        onClose();
    };
    const onBackClick = () => {
        onClose();
    };
    const totalPrice = React.useMemo(() => {
        const totalPrice = {
            amount: 0,
            currency: null
        };
        state.forEach(item => {
            var _a;
            if (item.fare.prices && item.status === NASStatus.New) {
                const travellerPrice = (_a = item.fare.prices.find(price => price.traveller.id === item.travellerId)) === null || _a === void 0 ? void 0 : _a.price;
                totalPrice.amount += travellerPrice.amount;
                totalPrice.currency = travellerPrice.currency;
            }
        });
        return totalPrice;
    }, [selectedServices, state]);
    React.useEffect(() => {
        if (defaultSegmentId) {
            setSegmentId(defaultSegmentId);
        }
    }, [defaultSegmentId]);
    React.useEffect(() => {
        const selectedServiceInSegment = state.find(item => item.product.segmentId.toString() === segmentId);
        if (selectedServiceInSegment) {
            setSelectedServiceId(selectedServiceInSegment.product.id);
        }
        else {
            setSelectedServiceId(null);
        }
    }, [segmentId]);
    const selectedTravellersInSegment = React.useMemo(() => {
        return state.filter(item => item.product.segmentId.toString() === segmentId);
    }, [state, segmentId]);
    const isDifferentSelectedServicesCount = React.useMemo(() => {
        const selectedServicesCount = selectedServices.reduce((count, service) => count + service.travellerIds.length, 0);
        return Math.abs(selectedServicesCount - state.length) > 0;
    }, [state, selectedServices]);
    const everyPortServicesInSegment = React.useMemo(() => {
        return everyPortServices.filter(service => service.segmentId.toString() === segmentId);
    }, [everyPortServices, segmentId]);
    const noAdultsInTravellers = React.useMemo(() => {
        const travellersOnSegment = state.filter(item => item.product.segmentId.toString() === segmentId);
        if (travellersOnSegment.length > 0) {
            return travellersOnSegment.every(travellerOnSegment => travellers.find(traveller => traveller.id === travellerOnSegment.travellerId).type !==
                PassengerType.Adult);
        }
        return false;
    }, [state, travellers, segmentId]);
    React.useEffect(() => {
        if (!selectedServiceId && everyPortServicesInSegment.length === 1) {
            setSelectedServiceId(everyPortServicesInSegment[0].id);
        }
    }, [selectedServiceId, everyPortServicesInSegment]);
    const [rulesOpen, setRulesOpen] = React.useState(false);
    const openRules = () => {
        var _a;
        if ((_a = selectedService.terms[0]) === null || _a === void 0 ? void 0 : _a.html) {
            setRulesOpen(true);
        }
        else {
            selectedService.terms.forEach(term => window.open(term.url, '_blank'));
        }
    };
    const closeRules = () => setRulesOpen(false);
    const isNewServicesExists = React.useMemo(() => {
        return state.some(product => product.status === NASStatus.New);
    }, [state]);
    return (React.createElement(React.Fragment, null,
        loading && React.createElement(SimpleLoader, null),
        React.createElement(MediaQuery, { minWidth: MOBILE_MIN_WIDTH },
            React.createElement(SegmentTabs, { title: header, activeSegment: segmentId, segments: tabs, onSegmentClick: setSegmentId, onClose: onClose, wrapperClassName: theme.segments, sticky: true })),
        React.createElement("div", { className: theme.inner },
            React.createElement("div", { className: theme.header },
                React.createElement("div", null,
                    format(segment.departure.date, 'dd MMM yyyy'),
                    " \u2014 ",
                    segment.departure.airport.name)),
            !!selectedService && (React.createElement(React.Fragment, null,
                React.createElement(Options, { product: selectedService, onRulesOpen: openRules }),
                React.createElement(Rules, { rules: selectedService.terms, onClose: closeRules, open: rulesOpen }))),
            React.createElement("div", { className: theme.container },
                !!selectedService && (React.createElement(SelectedService, { service: selectedService, travellers: travellers, onCancel: (everyPortServicesInSegment === null || everyPortServicesInSegment === void 0 ? void 0 : everyPortServicesInSegment.length) > 1 ? () => onServiceSelect(selectedService) : null, addTraveller: setTraveller, removeTraveller: removeTraveller, selectedTravellersInSegment: selectedTravellersInSegment, modifyAvailable: modifyAvailable })),
                !selectedServiceId &&
                    everyPortServicesInSegment.map(service => {
                        return (React.createElement(EveryPortCard, { key: service.id, options: service.facilities, price: findMinPrice(service, travellers), images: service.images, terminalName: service.terminalName, name: service.name, description: service.description, location: service.meetingPoint, onSelect: () => {
                                onServiceSelect(service);
                            }, category: service.category }));
                    }))),
        (isDifferentSelectedServicesCount || (state.length > 0 && isNewServicesExists)) && (React.createElement(Toolbar, { buttonDisabled: noAdultsInTravellers, totalPrice: totalPrice, onButtonClick: onNextClick, nextButtonText: t('Confirm'), onBackButtonClick: onBackClick }))));
};
export default Inner;
